import { Trans, useLingui } from "@lingui/react/macro";
import type {
  AgentSkillCatalogEntry,
  Bot,
  ComputerMode,
  Me,
  ModelBackupChoice,
  ModelCatalogEntry,
  ModelCredential,
  PlanSlots,
  ThinkingLevel,
  VoiceInfo,
} from "@rakazo/contracts";
import {
  BOT_COMPUTER_MODES,
  BOT_DESCRIPTION_MAX_LENGTH,
  BOT_NAME_MAX_LENGTH,
  BOT_TITLE_MAX_LENGTH,
  BUILTIN_TOOL_NAMES,
  connectedBackupOptions,
  isBuiltinToolName,
  isLiveVoiceId,
  LIVE_VOICES,
} from "@rakazo/contracts";
import {
  chatModelChoices,
  connectedModelChoices,
  credentialForModel,
  credentialThinkingLevels,
  modelOptionKey,
  parseModelOptionKey,
  resolveSelectableModelId,
  slotAvailability,
} from "@rakazo/core";
import {
  BotAvatar,
  Button,
  DEFAULT_BOT_AVATAR_COLOR,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Textarea,
  Toggle,
} from "@rakazo/ui-web";
import { Card } from "@rakazo/ui-web/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@rakazo/ui-web/components/ui/collapsible";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Download,
  Settings,
  Sparkles,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useEffect, useId, useRef, useState } from "react";
import { ErrorBoundary, SectionLoadFailed } from "../../components/ErrorBoundary";
import { LiveVoiceKeyCard } from "../../components/live/LiveVoiceKeyCard";
import { botProfilePatch } from "../../lib/bot-profile-patch";
import { liveVoiceForBot, setLiveVoiceForBot } from "../../lib/live-voice-preference";
import { thinkingLevelLabel } from "../../lib/model-catalog";
import { slotUnavailableText, tierLimitErrorText } from "../../lib/plan-slots";
import { lazyComponent } from "../../lib/preload-recovery";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { BackupModelList } from "../BackupModelList";
import { AvatarStudioPopover } from "./avatar-studio-popover";
import { BotCredentialsSection } from "./bot-credentials";
import { BotProfileText } from "./bot-profile-text";
import { ChatModelPicker } from "./chat-model-picker";

const ScratchpadSection = lazyComponent(() =>
  import("../ScratchpadSection").then((module) => module.ScratchpadSection),
);

const KnowledgeSection = lazyComponent(() =>
  import("../KnowledgeSection").then((module) => module.KnowledgeSection),
);

const rowLabelClass = "text-base text-foreground";

function ComputerModePicker({
  value,
  onChange,
  teamTestId,
  privateTestId,
  showLabel = true,
  plan,
}: {
  value: ComputerMode;
  onChange: (value: ComputerMode) => void;
  teamTestId?: string;
  privateTestId?: string;
  showLabel?: boolean;
  /** When given, a mode the plan has no room for stays visible but cannot be chosen. */
  plan?: PlanSlots | null;
}) {
  const noteId = useId();
  const [tried, setTried] = useState<ComputerMode | null>(null);
  const availability = (mode: ComputerMode) => (plan ? slotAvailability(plan, mode) : "open");
  const why = tried ? availability(tried) : "open";
  return (
    <div className={showLabel ? "mt-4" : undefined}>
      {showLabel ? (
        <div className="text-sm text-foreground">
          <Trans>Computer</Trans>
        </div>
      ) : null}
      <div className={`${showLabel ? "mt-2 " : ""}grid grid-cols-2 gap-2`}>
        {BOT_COMPUTER_MODES.map((mode) => {
          const unavailable = availability(mode) !== "open";
          return (
            <Toggle
              type="button"
              key={mode}
              variant="outline"
              pressed={value === mode}
              data-testid={mode === "team" ? teamTestId : privateTestId}
              // Not `disabled`: the control keeps focus and still takes a click, which explains why.
              aria-disabled={unavailable ? true : undefined}
              aria-describedby={tried === mode && why !== "open" ? noteId : undefined}
              onPressedChange={(pressed) => {
                if (unavailable) {
                  setTried(mode);
                } else if (pressed) {
                  setTried(null);
                  onChange(mode);
                }
              }}
              className="min-h-11 text-base capitalize aria-disabled:text-muted-foreground aria-disabled:opacity-60 aria-pressed:border-foreground/40 aria-pressed:text-foreground"
            >
              {mode === "team" ? <Trans>Team</Trans> : <Trans>Private</Trans>}
            </Toggle>
          );
        })}
      </div>
      <div id={noteId} role="status">
        {tried && why !== "open" ? (
          <p className="mt-2 text-base text-muted-foreground">{slotUnavailableText(why)}</p>
        ) : null}
      </div>
    </div>
  );
}

/** Who the bot will be: the one that does the work, or the one that hands it on. */
function BotRolePicker({
  value,
  onChange,
  workerMissing,
}: {
  value: "worker" | "coordinator";
  onChange: (value: "worker" | "coordinator") => void;
  /** No bot on the team does work yet, so a coordinator would have no one to hand it to. */
  workerMissing: boolean;
}) {
  const noteId = useId();
  const noteShown = value === "coordinator" && workerMissing;
  return (
    <div className="mt-5">
      <div className="text-sm font-medium text-foreground">
        <Trans>Role</Trans>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {(["worker", "coordinator"] as const).map((role) => (
          <Toggle
            type="button"
            key={role}
            variant="outline"
            pressed={value === role}
            data-testid={`create-bot-role-${role}`}
            aria-describedby={role === "coordinator" && noteShown ? noteId : undefined}
            onPressedChange={(pressed) => {
              if (pressed) onChange(role);
            }}
            className="min-h-11 text-base aria-pressed:border-foreground/40 aria-pressed:text-foreground"
          >
            {role === "worker" ? <Trans>Worker</Trans> : <Trans>Coordinator</Trans>}
          </Toggle>
        ))}
      </div>
      {noteShown ? (
        <p id={noteId} className="mt-2 text-sm text-muted-foreground">
          <Trans>A coordinator needs at least one worker. You create it next.</Trans>
        </p>
      ) : null}
    </div>
  );
}

export function CreateBotForm({
  onCreate,
  onImport,
  onCancel,
  plan,
  workerMissing = false,
  initialRole = "worker",
  notice,
}: {
  onCreate: (input: {
    name: string;
    title: string;
    description: string;
    computerMode: ComputerMode;
    coordinatorOnly: boolean;
  }) => Promise<void>;
  /** Creates an assistant from a file exported earlier; without it the form has no import. */
  onImport?: (file: File) => Promise<void>;
  onCancel: () => void;
  /** The person's plan slots; without them every computer mode stays available. */
  plan?: PlanSlots | null;
  /** The team has no bot that does work, so a coordinator made now has no one to hand it to. */
  workerMissing?: boolean;
  initialRole?: "worker" | "coordinator";
  /** One line above the form, such as why a second bot is being made. */
  notice?: string;
}) {
  const { t } = useLingui();
  const ids = useId();
  const [role, setRole] = useState<"worker" | "coordinator">(initialRole);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Team is the default: Private slots are limited per plan and a free plan has none.
  const [computerMode, setComputerMode] = useState<ComputerMode>("team");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!name.trim() || submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setSubmitting(true);
    try {
      await onCreate({
        name: name.trim(),
        title: title.trim(),
        description: description.trim(),
        computerMode,
        coordinatorOnly: role === "coordinator",
      });
    } catch (err) {
      setError(tierLimitErrorText(err) ?? errorText(err, t`Could not create assistant`));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  async function handleImport(file: File) {
    if (!onImport || submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setSubmitting(true);
    try {
      await onImport(file);
    } catch (err) {
      setError(tierLimitErrorText(err) ?? errorText(err, t`Could not create assistant`));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <form
      className="mx-auto w-full max-w-lg"
      data-testid="create-bot-form"
      aria-busy={submitting}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <fieldset disabled={submitting} className="m-0 min-w-0 border-0 p-0">
        <div className="relative mb-6 rounded-3xl bg-muted px-5 py-6 text-center">
          <div
            aria-hidden="true"
            className="relative mx-auto mb-4 flex size-20 items-center justify-center rounded-full border border-border bg-background shadow-sm"
          >
            <BotAvatar
              color={DEFAULT_BOT_AVATAR_COLOR}
              identity="new-assistant"
              size={60}
              variant="robot"
            />
            <span className="absolute -right-1 top-0 flex size-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground">
              <Sparkles size={15} strokeWidth={1.6} />
            </span>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            <Trans>New assistant</Trans>
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute right-2 top-2 min-h-11 min-w-11 rounded-full text-muted-foreground"
            disabled={submitting}
            aria-label={t`Cancel new assistant`}
            onClick={onCancel}
          >
            <X size={16} strokeWidth={1.8} />
          </Button>
        </div>
        {notice ? (
          <p role="status" className="mb-5 text-base text-muted-foreground">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            data-testid="create-bot-error"
            className="mb-5 rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-base text-destructive"
          >
            {error}
          </p>
        ) : null}
        <label htmlFor={`${ids}-name`} className="block text-sm font-medium text-foreground">
          <Trans>Name</Trans>
          <Input
            id={`${ids}-name`}
            value={name}
            maxLength={BOT_NAME_MAX_LENGTH}
            onChange={(e) => setName(e.target.value)}
            placeholder={t`Name your assistant`}
            autoComplete="off"
            required
            className="mt-2 min-h-12 rounded-2xl border-border bg-background px-4 text-base md:text-base"
          />
        </label>
        <label
          htmlFor={`${ids}-description`}
          className="mt-5 block text-sm font-medium text-foreground"
        >
          <Trans>What would you like help with?</Trans>
          <Textarea
            id={`${ids}-description`}
            value={description}
            maxLength={BOT_DESCRIPTION_MAX_LENGTH}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t`For example, planning my day or writing emails`}
            rows={3}
            className="mt-2 min-h-28 resize-y rounded-2xl border-border bg-background px-4 py-3 font-normal text-base leading-relaxed md:text-base"
          />
        </label>
        <BotRolePicker value={role} onChange={setRole} workerMissing={workerMissing} />
        <Collapsible
          open={optionsOpen}
          onOpenChange={setOptionsOpen}
          className="mt-5 rounded-2xl border border-border bg-muted/40 px-4"
        >
          <CollapsibleTrigger
            type="button"
            data-testid="create-bot-options"
            className="group flex min-h-12 w-full items-center justify-between gap-2 rounded-lg text-start text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trans>More options</Trans>
            <ChevronDown
              aria-hidden="true"
              className="size-4 -rotate-90 transition-transform group-aria-expanded:rotate-0 motion-reduce:transition-none"
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="pb-4">
            <label
              htmlFor={`${ids}-title`}
              className="mt-5 block text-sm font-medium text-foreground"
            >
              <Trans>Short label (optional)</Trans>
              <Input
                id={`${ids}-title`}
                value={title}
                maxLength={BOT_TITLE_MAX_LENGTH}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t`For example, Personal assistant`}
                className="mt-2 min-h-12 rounded-2xl border-border bg-background px-4 text-base md:text-base"
              />
            </label>
            <div data-testid="create-bot-computer">
              <ComputerModePicker
                value={computerMode}
                onChange={setComputerMode}
                teamTestId="create-bot-team"
                privateTestId="create-bot-private"
                plan={plan}
              />
            </div>
            {onImport ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".gz,.tgz,application/gzip"
                  hidden
                  data-testid="create-bot-import-file"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    event.currentTarget.value = "";
                    if (file) void handleImport(file);
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="create-bot-import"
                  className="mt-4 min-h-11 w-full justify-start px-0 text-base text-muted-foreground"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Trans>Import from file</Trans>
                </Button>
              </>
            ) : null}
          </CollapsibleContent>
        </Collapsible>
        <Button
          type="submit"
          className="mt-6 min-h-12 w-full gap-2 rounded-full text-base shadow-sm transition-transform motion-safe:active:scale-[0.96] motion-reduce:transform-none"
          disabled={!name.trim() || submitting}
        >
          {submitting ? <Trans>Creating…</Trans> : <Trans>Create assistant</Trans>}
          {!submitting ? <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" /> : null}
        </Button>
      </fieldset>
    </form>
  );
}

export function BotSettings({
  bot,
  memoryProviderConfigured,
  onSkillsChange,
  onSave,
  onExport,
  onClear,
  peers = [],
  onOpenPeer,
  focusModel = 0,
  onOpenConnections,
}: {
  bot: Bot;
  /** Bots this chat has exchanged messages with; the exchange itself stays out of the chat. */
  peers?: readonly { peerBotId: string; peerBotName: string; color: string }[];
  onOpenPeer?: (peer: { peerBotId: string; peerBotName: string }) => void;
  focusModel?: number;
  onOpenConnections?: () => void;
  onSkillsChange: (skills: AgentSkillCatalogEntry[]) => void;
  memoryProviderConfigured: boolean;
  onSave: (patch: {
    name?: string;
    title?: string;
    description?: string;
    instructions?: string;
    color?: string;
    notifyOnFinish?: boolean;
    computerMode?: ComputerMode;
    memoryScope?: "isolated" | "shared" | null;
    autoSpeak?: boolean;
    fleetManagement?: boolean;
    coordinatorOnly?: boolean;
    voiceId?: string | null;
    modelProvider?: string | null;
    modelId?: string | null;
    backupModels?: ModelBackupChoice[] | null;
    thinkingLevel?: ThinkingLevel | null;
    disabledBuiltinTools?: string[];
  }) => Promise<void>;
  onExport: () => Promise<void>;
  onClear: () => void;
}) {
  const { t } = useLingui();
  const ids = useId();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const modelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusModel) {
      modelRef.current?.scrollIntoView?.({ block: "nearest" });
      modelRef.current?.querySelector<HTMLElement>('[id$="-model"]')?.focus();
    }
  }, [focusModel]);
  const [name, setName] = useState(bot.name);
  const [title, setTitle] = useState(bot.title);
  const savedNameRef = useRef(bot.name);
  const savedTitleRef = useRef(bot.title);
  const [description, setDescription] = useState(bot.description);
  // A roster refresh can skip replacing bots while a reorder is in flight, so
  // this prop keeps the description from when the panel opened. Later saves
  // compare against the description last saved here; otherwise a model or
  // voice change treats that stale text as an edit and overwrites instructions.
  const savedDescriptionRef = useRef(bot.description ?? "");
  const [color, setColor] = useState(bot.color);
  const [notifyOnFinish, setNotifyOnFinish] = useState(bot.notifyOnFinish ?? true);
  const acceptedAutomatic = useRef({
    color: bot.color,
    notifyOnFinish: bot.notifyOnFinish ?? true,
  });
  const automaticRevision = useRef({ color: 0, notifyOnFinish: 0 });
  const [computerMode, setComputerMode] = useState(bot.computerMode);
  const [memoryScope, setMemoryScope] = useState(bot.memoryScope);
  const [autoSpeak, setAutoSpeak] = useState(bot.autoSpeak);
  const [fleetManagement, setFleetManagement] = useState(bot.fleetManagement);
  useEffect(() => setFleetManagement(bot.fleetManagement), [bot.fleetManagement]);
  const [coordinatorOnly, setCoordinatorOnly] = useState(bot.coordinatorOnly);
  // The server turns the role off when the bot is given work to do; say so once, not silently.
  const [roleNotice, setRoleNotice] = useState<string | null>(null);
  const previousCoordinatorOnly = useRef(bot.coordinatorOnly);
  // The person's own switch change, so only a change they did not make is announced.
  const roleChangedByPerson = useRef(false);
  useEffect(() => {
    if (previousCoordinatorOnly.current && !bot.coordinatorOnly && !roleChangedByPerson.current)
      setRoleNotice(t`This bot now does work itself, so it is no longer a coordinator`);
    roleChangedByPerson.current = false;
    previousCoordinatorOnly.current = bot.coordinatorOnly;
    setCoordinatorOnly(bot.coordinatorOnly);
  }, [bot.coordinatorOnly, t]);
  const [voiceId, setVoiceId] = useState(bot.voiceId ?? "");
  const [liveVoiceId, setLiveVoiceId] = useState(() => liveVoiceForBot(bot.id));
  useEffect(() => setLiveVoiceId(liveVoiceForBot(bot.id)), [bot.id]);
  const [voices, setVoices] = useState<VoiceInfo[]>([]);
  const [modelKey, setModelKey] = useState(
    bot.modelProvider && bot.modelId ? modelOptionKey(bot.modelProvider, bot.modelId) : "",
  );
  const [thinkingLevel, setThinkingLevel] = useState(bot.thinkingLevel ?? "");
  // The bot's own backup list as edited here; null until the person touches it.
  const [backupsDraft, setBackupsDraft] = useState<ModelBackupChoice[] | null>(null);
  // The person's default list, loaded only when a bot needs it to start its own list from.
  const [defaultBackups, setDefaultBackups] = useState<ModelBackupChoice[] | null>(null);
  const [disabledBuiltinTools, setDisabledBuiltinTools] = useState<string[]>(
    bot.disabledBuiltinTools ?? [],
  );
  // The list on screen is updated before the save returns. This stays on the
  // last list the server accepted so a rejection can undo that preview.
  const persistedDisabledToolsRef = useRef(disabledBuiltinTools);
  const serverDisabledToolsRef = useRef(disabledBuiltinTools);
  const pendingSavesRef = useRef(0);
  useEffect(() => {
    const next = bot.disabledBuiltinTools ?? [];
    if (serverDisabledToolsRef.current === next) return;
    serverDisabledToolsRef.current = next;
    persistedDisabledToolsRef.current = next;
    if (pendingSavesRef.current === 0) setDisabledBuiltinTools(next);
  }, [bot.disabledBuiltinTools]);
  const [credentials, setCredentials] = useState<ModelCredential[]>([]);
  const [catalog, setCatalog] = useState<ModelCatalogEntry[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [modelMetaReady, setModelMetaReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAdvanced, setSavedAdvanced] = useState(() =>
    JSON.stringify([
      bot.modelProvider && bot.modelId ? modelOptionKey(bot.modelProvider, bot.modelId) : "",
      bot.thinkingLevel ?? "",
      bot.computerMode,
      bot.memoryScope,
      bot.autoSpeak,
      bot.voiceId ?? "",
      null,
    ]),
  );
  const advancedSnapshot = JSON.stringify([
    modelKey,
    thinkingLevel,
    computerMode,
    memoryScope,
    autoSpeak,
    voiceId,
    backupsDraft,
  ]);
  const dirty =
    name.trim() !== savedNameRef.current.trim() ||
    title.trim() !== savedTitleRef.current.trim() ||
    advancedSnapshot !== savedAdvanced ||
    description.trim() !== savedDescriptionRef.current;
  const [error, setError] = useState<string | null>(null);
  const saveQueueRef = useRef(Promise.resolve());
  const executeSaveRef = useRef<
    (
      patchOverrides?: {
        name?: string;
        title?: string;
        description?: string;
        color?: string;
        notifyOnFinish?: boolean;
        fleetManagement?: boolean;
        coordinatorOnly?: boolean;
        disabledBuiltinTools?: string[];
        baseDisabledBuiltinTools?: string[];
      },
      revisions?: { color: number; notifyOnFinish: number },
    ) => Promise<void>
  >(async () => undefined);
  useEffect(() => {
    void rpc.voice
      .voices({})
      .then(setVoices)
      .catch(() => setVoices([]));
    void Promise.all([rpc.models.credentials(), rpc.models.list(), rpc.me()])
      .then(([nextCredentials, nextCatalog, nextMe]) => {
        setCredentials(nextCredentials);
        setCatalog(nextCatalog);
        setMe(nextMe);
        // Only mark ready on success — a failed catalog load must not clear
        // an existing thinkingLevel override on save.
        setModelMetaReady(true);
      })
      .catch(() => undefined);
  }, []);

  const connectedOptions = connectedModelChoices(credentials, catalog);
  const backupOptions = connectedBackupOptions(catalog, credentials);
  const storedModel = modelKey ? parseModelOptionKey(modelKey) : null;
  const selectedModel = storedModel
    ? {
        provider: storedModel.provider,
        modelId: resolveSelectableModelId(catalog, storedModel.provider, storedModel.modelId),
      }
    : null;
  const selectedModelKey = selectedModel
    ? modelOptionKey(selectedModel.provider, selectedModel.modelId)
    : "";

  // A bot with a model of its own has backup models of its own. One saved before bots had lists
  // keeps following the default list until its list is edited here.
  const ownsConfiguration = Boolean(selectedModel);
  const followsDefaultList = bot.backupModels == null;
  const needsDefaultBackups = ownsConfiguration && followsDefaultList && backupsDraft === null;
  useEffect(() => {
    if (!needsDefaultBackups || defaultBackups !== null) return;
    let cancelled = false;
    void Promise.resolve()
      .then(() => rpc.models.backups({}))
      .then((list) => {
        if (!cancelled) setDefaultBackups(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [needsDefaultBackups, defaultBackups]);
  const shownBackups = backupsDraft ?? bot.backupModels ?? defaultBackups ?? [];

  const defaultEntry = me?.defaultModel
    ? catalogEntry(catalog, me.defaultProvider, me.defaultModel)
    : undefined;
  const modelChoices = chatModelChoices(
    connectedOptions,
    selectedModelKey,
    t`Space default`,
    defaultEntry?.images,
  );

  const effectiveProvider = selectedModel?.provider ?? me?.defaultProvider ?? null;
  const effectiveModelId = selectedModel?.modelId ?? me?.defaultModel ?? null;
  const effectiveEntry =
    effectiveProvider && effectiveModelId
      ? catalog.find(
          (entry) =>
            entry.provider === effectiveProvider &&
            resolveSelectableModelId(catalog, entry.provider, entry.id) === effectiveModelId,
        )
      : undefined;
  const effectiveCredential = credentialForModel(credentials, effectiveProvider, effectiveModelId);
  const thinkingOptions = (
    credentialThinkingLevels(effectiveCredential, effectiveModelId) ??
    effectiveEntry?.thinkingLevels ??
    []
  ).filter((level) => level !== "off");
  const defaultThinkingLevel = effectiveCredential?.thinkingLevel ?? "medium";

  async function executeSave(
    patchOverrides?: {
      name?: string;
      title?: string;
      description?: string;
      color?: string;
      notifyOnFinish?: boolean;
      fleetManagement?: boolean;
      coordinatorOnly?: boolean;
      disabledBuiltinTools?: string[];
      baseDisabledBuiltinTools?: string[];
    },
    revisions?: { color: number; notifyOnFinish: number },
  ) {
    try {
      setSaving(true);
      setError(null);
      if (patchOverrides?.fleetManagement !== undefined) {
        await onSave({ fleetManagement: patchOverrides.fleetManagement });
        return;
      }
      if (patchOverrides?.coordinatorOnly !== undefined) {
        await onSave({ coordinatorOnly: patchOverrides.coordinatorOnly });
        return;
      }
      const selected = selectedModel;
      const nextName = (patchOverrides?.name !== undefined ? patchOverrides.name : name).trim();
      const nextTitle = (patchOverrides?.title !== undefined ? patchOverrides.title : title).trim();
      const nextDescription = (
        patchOverrides?.description !== undefined ? patchOverrides.description : description
      ).trim();
      const nextColor = patchOverrides?.color !== undefined ? patchOverrides.color : color;
      const nextNotify =
        patchOverrides?.notifyOnFinish !== undefined
          ? patchOverrides.notifyOnFinish
          : notifyOnFinish;

      if (nextName) setName(nextName);
      setTitle(nextTitle);
      setDescription(nextDescription);
      const patch = {
        ...(nextName && nextName !== savedNameRef.current.trim() ? { name: nextName } : {}),
        ...(nextTitle !== savedTitleRef.current.trim() ? { title: nextTitle } : {}),
        // Keep untouched profile fields off the wire.
        ...botProfilePatch(savedDescriptionRef.current, nextDescription),
        // Unchanged color stays off the wire so a legacy named value cannot fail a name save.
        ...(nextColor !== acceptedAutomatic.current.color ? { color: nextColor } : {}),
        notifyOnFinish: nextNotify,
        computerMode,
        memoryScope,
        autoSpeak,
        voiceId: voiceId || null,
        modelProvider: selected?.provider ?? null,
        modelId: selected?.modelId ?? null,
        ...backupsPatch(selected, bot, backupsDraft, defaultBackups),
        ...(modelMetaReady &&
        (thinkingOptions.length > 0 ||
          (selected?.provider ?? null) !== bot.modelProvider ||
          (selected?.modelId ?? null) !== bot.modelId)
          ? {
              thinkingLevel: thinkingOptions.length
                ? ((thinkingLevel || null) as ThinkingLevel | null)
                : null,
            }
          : {}),
      };
      const requestedTools = patchOverrides?.disabledBuiltinTools;
      const baseTools = patchOverrides?.baseDisabledBuiltinTools ?? [];
      // Preserve this edit's additions/removals against any refreshed server list.
      // Retry if a refresh arrives while the request is in flight.
      for (;;) {
        const serverTools = serverDisabledToolsRef.current;
        const nextTools =
          requestedTools === undefined
            ? undefined
            : [
                ...persistedDisabledToolsRef.current.filter(
                  (name) => !baseTools.includes(name) || requestedTools.includes(name),
                ),
                ...requestedTools.filter(
                  (name) =>
                    !baseTools.includes(name) && !persistedDisabledToolsRef.current.includes(name),
                ),
              ];
        // Unrelated saves omit the list, preserving changes from other sessions.
        await onSave({
          ...patch,
          ...(nextTools !== undefined ? { disabledBuiltinTools: nextTools } : {}),
        });
        if (nextTools === undefined) break;
        if (
          serverTools !== serverDisabledToolsRef.current &&
          !sameStringList(nextTools, serverDisabledToolsRef.current)
        )
          continue;
        persistedDisabledToolsRef.current = nextTools;
        break;
      }
      acceptedAutomatic.current = { color: nextColor, notifyOnFinish: nextNotify };
      if (nextName) savedNameRef.current = nextName;
      savedTitleRef.current = nextTitle;
      savedDescriptionRef.current = nextDescription;
      setSavedAdvanced(
        JSON.stringify([
          modelKey,
          thinkingLevel,
          computerMode,
          memoryScope,
          autoSpeak,
          voiceId,
          null,
        ]),
      );
      setBackupsDraft(null);
    } catch (err) {
      if (
        patchOverrides?.color !== undefined &&
        revisions?.color === automaticRevision.current.color
      )
        setColor(acceptedAutomatic.current.color);
      if (
        patchOverrides?.notifyOnFinish !== undefined &&
        revisions?.notifyOnFinish === automaticRevision.current.notifyOnFinish
      )
        setNotifyOnFinish(acceptedAutomatic.current.notifyOnFinish);
      if (patchOverrides?.fleetManagement !== undefined)
        setFleetManagement(!patchOverrides.fleetManagement);
      if (patchOverrides?.coordinatorOnly !== undefined) {
        roleChangedByPerson.current = false;
        setCoordinatorOnly(!patchOverrides.coordinatorOnly);
      }
      setError(errorText(err, t`Could not save`));
    } finally {
      pendingSavesRef.current -= 1;
      if (pendingSavesRef.current === 0) {
        setDisabledBuiltinTools([...persistedDisabledToolsRef.current]);
      }
      setSaving(pendingSavesRef.current > 0);
    }
  }
  executeSaveRef.current = executeSave;

  function disableBuiltinTool(raw: string) {
    const name = raw.trim();
    if (!name) return;
    if (!isBuiltinToolName(name)) return;
    setError(null);
    if (disabledBuiltinTools.includes(name)) return;
    const next = [...disabledBuiltinTools, name];
    setDisabledBuiltinTools(next);
    void enqueueSave({ disabledBuiltinTools: next });
  }

  function enqueueSave(patchOverrides?: {
    name?: string;
    title?: string;
    description?: string;
    color?: string;
    notifyOnFinish?: boolean;
    fleetManagement?: boolean;
    coordinatorOnly?: boolean;
    disabledBuiltinTools?: string[];
  }) {
    // Serialize full-object auto-saves so an older in-flight request cannot
    // finish after a newer one and clobber fields. Always call through a ref so
    // queued work reads the latest field values, not a stale render closure.
    pendingSavesRef.current += 1;
    if (patchOverrides?.color !== undefined) automaticRevision.current.color += 1;
    if (patchOverrides?.notifyOnFinish !== undefined) automaticRevision.current.notifyOnFinish += 1;
    const revisions = { ...automaticRevision.current };
    const queuedPatch =
      patchOverrides?.disabledBuiltinTools === undefined
        ? patchOverrides
        : {
            ...patchOverrides,
            baseDisabledBuiltinTools: [...persistedDisabledToolsRef.current],
          };
    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(() => executeSaveRef.current(queuedPatch, revisions));
    return saveQueueRef.current;
  }

  return (
    <div data-testid="bot-settings" className="mx-auto w-full min-w-0 max-w-2xl space-y-4">
      <div data-testid="bot-profile-header" className="flex items-center gap-4 pt-1">
        <div className="shrink-0">
          <AvatarStudioPopover
            value={color}
            identity={bot.id}
            status={bot.status}
            size={56}
            onChange={(newColor) => {
              setColor(newColor);
              void enqueueSave({ color: newColor });
            }}
          />
        </div>
        <div
          data-testid="bot-profile-identity"
          className="flex min-w-0 flex-1 flex-col items-start"
        >
          <InlineProfileField
            label={t`Name`}
            editLabel={t`Edit name`}
            value={name}
            maxLength={BOT_NAME_MAX_LENGTH}
            className="text-xl font-semibold"
            onCommit={(value) => {
              if (!value.trim()) return;
              setName(value.trim());
              void enqueueSave({ name: value });
            }}
          />
          <InlineProfileField
            label={t`Title`}
            editLabel={t`Edit label`}
            placeholder={t`Add a label`}
            value={title}
            maxLength={BOT_TITLE_MAX_LENGTH}
            className="text-base text-muted-foreground"
            onCommit={(value) => {
              setTitle(value.trim());
              void enqueueSave({ title: value });
            }}
          />
        </div>
      </div>
      <Card className="gap-0 px-5 py-4">
        <BotProfileText
          bot={bot}
          description={description}
          descriptionId={`${ids}-description`}
          onDescriptionChange={setDescription}
          onDescriptionCommit={() => void enqueueSave()}
        />
      </Card>
      <SettingsGroup label={t`Notifications`}>
        <div className="flex min-h-11 items-center justify-between gap-3">
          <label
            htmlFor={`${ids}-notify-finish`}
            id={`${ids}-notify-finish-label`}
            className={rowLabelClass}
          >
            <Trans>When it finishes or needs input</Trans>
          </label>
          <Switch
            id={`${ids}-notify-finish`}
            checked={notifyOnFinish}
            aria-labelledby={`${ids}-notify-finish-label`}
            onCheckedChange={(checked) => {
              setNotifyOnFinish(checked);
              void enqueueSave({ notifyOnFinish: checked });
            }}
          />
        </div>
      </SettingsGroup>
      <ErrorBoundary fallback={<SectionLoadFailed />}>
        <Suspense fallback={null}>
          <ScratchpadSection botId={bot.id} />
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary fallback={<SectionLoadFailed />}>
        <Suspense fallback={null}>
          <KnowledgeSection botId={bot.id} onSkillsChange={onSkillsChange} />
        </Suspense>
      </ErrorBoundary>
      <div ref={modelRef}>
        <SettingsGroup
          label={t`Model`}
          action={
            onOpenConnections ? (
              <Button
                variant="ghost"
                size="icon-xs"
                className="[@media(pointer:coarse)]:size-11"
                aria-label={t`Model connections`}
                onClick={onOpenConnections}
              >
                <Settings className="size-4" />
              </Button>
            ) : null
          }
        >
          <div className="flex min-h-11 items-center justify-between gap-3">
            <label htmlFor={`${ids}-model`} className="sr-only">
              <Trans>Model</Trans>
            </label>
            <div className="min-w-0 w-full [&>div]:mt-0 [&>button]:mt-0">
              <ChatModelPicker
                id={`${ids}-model`}
                choices={modelChoices}
                value={selectedModelKey}
                onChange={(key) => {
                  setModelKey(key);
                  setThinkingLevel("");
                  if (!key) setBackupsDraft(null);
                }}
              />
            </div>
          </div>
          {thinkingOptions.length ? (
            <label
              htmlFor={`${ids}-thinking`}
              className="flex min-h-11 items-center justify-between gap-3 text-sm text-foreground"
            >
              <Trans>Thinking</Trans>
              <NativeSelect
                id={`${ids}-thinking`}
                className="max-w-2/3"
                value={thinkingLevel}
                onChange={(event) => setThinkingLevel(event.target.value)}
              >
                <NativeSelectOption value="">
                  {t`Default (${thinkingLevelLabel(defaultThinkingLevel)})`}
                </NativeSelectOption>
                {thinkingOptions.map((level) => (
                  <NativeSelectOption key={level} value={level}>
                    {thinkingLevelLabel(level)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </label>
          ) : null}
          {ownsConfiguration ? (
            <div>
              <div className="text-sm text-foreground">
                <Trans>Backup models</Trans>
              </div>
              <BackupModelList
                backups={shownBackups}
                options={backupOptions}
                catalog={catalog}
                editable={modelMetaReady}
                onChange={setBackupsDraft}
              />
            </div>
          ) : null}
        </SettingsGroup>
      </div>
      <SettingsGroup label={t`Computer`}>
        <ComputerModePicker showLabel={false} value={computerMode} onChange={setComputerMode} />
        {memoryProviderConfigured ? (
          <div className="mt-4 text-sm text-foreground">
            <Trans>Memory scope</Trans>
            <div className="mt-2 flex gap-2">
              {(
                [
                  { value: null, label: t`Inherit default` },
                  { value: "isolated" as const, label: t`Isolated` },
                  { value: "shared" as const, label: t`Shared` },
                ] satisfies Array<{ value: "isolated" | "shared" | null; label: string }>
              ).map((option) => (
                <Toggle
                  key={option.label}
                  variant="outline"
                  size="sm"
                  pressed={memoryScope === option.value}
                  onPressedChange={(pressed) => {
                    if (pressed) setMemoryScope(option.value);
                  }}
                  className="flex-1 aria-pressed:border-foreground/40 aria-pressed:text-foreground [@media(pointer:coarse)]:min-h-11"
                >
                  {option.label}
                </Toggle>
              ))}
            </div>
          </div>
        ) : null}
      </SettingsGroup>
      <SettingsGroup label={t`Voice`}>
        <label
          htmlFor={`${ids}-auto-speak`}
          className="flex min-h-11 cursor-pointer items-center justify-between gap-3 text-sm text-foreground"
        >
          <span className={rowLabelClass}>
            <Trans>Read replies aloud</Trans>
          </span>
          <Switch
            id={`${ids}-auto-speak`}
            checked={autoSpeak}
            onCheckedChange={(checked) => setAutoSpeak(checked)}
          />
        </label>
        {voices.length ? (
          <label
            htmlFor={`${ids}-voice`}
            className="flex min-h-11 items-center justify-between gap-3 text-sm text-foreground"
          >
            <Trans>Read-aloud voice</Trans>
            <NativeSelect
              id={`${ids}-voice`}
              className="max-w-2/3"
              value={voiceId}
              onChange={(event) => setVoiceId(event.target.value)}
            >
              <NativeSelectOption value="">{t`Account default`}</NativeSelectOption>
              {voices.map((voice) => (
                <NativeSelectOption key={voice.id} value={voice.id}>
                  {voice.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </label>
        ) : null}
        <label
          htmlFor={`${ids}-live-voice`}
          className="flex min-h-11 items-center justify-between gap-3 text-sm text-foreground"
        >
          <Trans>Live voice</Trans>
          <NativeSelect
            id={`${ids}-live-voice`}
            className="max-w-2/3"
            value={liveVoiceId}
            onChange={(event) => {
              const next = event.target.value;
              if (!isLiveVoiceId(next)) return;
              setLiveVoiceId(next);
              setLiveVoiceForBot(bot.id, next);
            }}
          >
            {LIVE_VOICES.map((voice) => (
              <NativeSelectOption key={voice.id} value={voice.id}>
                {voice.name} · {voice.gender === "female" ? t`Female` : t`Male`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </label>
        <p className="text-xs leading-[1.5] text-muted-foreground">
          <Trans>Live voice is stored on this device and used only in live conversation.</Trans>
        </p>
      </SettingsGroup>
      <LiveVoiceKeyCard />
      <SettingsGroup label={t`Permissions`}>
        <div className="flex min-h-11 items-center justify-between gap-3 text-sm">
          <div>
            <label htmlFor={`${ids}-fleet-management`} className={rowLabelClass}>
              <Trans>Manage the bots it creates</Trans>
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              <Trans>Can edit and organise bots it creates</Trans>
            </p>
          </div>
          <Switch
            id={`${ids}-fleet-management`}
            checked={fleetManagement}
            disabled={saving}
            onCheckedChange={(checked) => {
              setFleetManagement(checked);
              void enqueueSave({ fleetManagement: checked });
            }}
          />
        </div>
        <div className="mt-3 flex min-h-11 items-center justify-between gap-3 text-sm">
          <div>
            <label htmlFor={`${ids}-coordinator`} className={rowLabelClass}>
              <Trans>Coordinator</Trans>
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              <Trans>Checks information and delegates execution</Trans>
            </p>
          </div>
          <Switch
            id={`${ids}-coordinator`}
            checked={coordinatorOnly}
            disabled={saving}
            onCheckedChange={(checked) => {
              setRoleNotice(null);
              setCoordinatorOnly(checked);
              roleChangedByPerson.current = true;
              void enqueueSave({ coordinatorOnly: checked });
            }}
          />
        </div>
        {roleNotice ? (
          <p role="status" className="mt-2 text-xs text-muted-foreground">
            {roleNotice}
          </p>
        ) : null}
      </SettingsGroup>
      <SettingsGroup label={t`Tools`}>
        <section data-testid="bot-settings-tools">
          <div className="space-y-4 [&>*]:mt-0">
            <div>
              <NativeSelect
                id={`${ids}-disabled-tools`}
                aria-label={t`Disable a tool`}
                value=""
                onChange={(event) => disableBuiltinTool(event.target.value)}
                className="w-full"
              >
                <NativeSelectOption value="">{t`Disable a tool`}</NativeSelectOption>
                {BUILTIN_TOOL_NAMES.filter((name) => !disabledBuiltinTools.includes(name)).map(
                  (name) => (
                    <NativeSelectOption key={name} value={name}>
                      {humanizeTool(name)}
                    </NativeSelectOption>
                  ),
                )}
              </NativeSelect>
            </div>
            {disabledBuiltinTools.map((toolName) => (
              <div
                key={toolName}
                className="flex min-h-11 items-center justify-between gap-3 text-sm"
              >
                <span>{humanizeTool(toolName)}</span>
                <Button
                  id={`${ids}-disabled-${toolName}`}
                  variant="ghost"
                  size="icon-xs"
                  className="[@media(pointer:coarse)]:size-11"
                  aria-label={t`Enable ${humanizeTool(toolName)}`}
                  title={t`Enable ${humanizeTool(toolName)}`}
                  onClick={() => {
                    const next = disabledBuiltinTools.filter((entry) => entry !== toolName);
                    setDisabledBuiltinTools(next);
                    void enqueueSave({ disabledBuiltinTools: next });
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      </SettingsGroup>
      <BotCredentialsSection botId={bot.id} />
      <ErrorBoundary fallback={<SectionLoadFailed />}>
        <Suspense fallback={null}>
          <KnowledgeSection botId={bot.id} onSkillsChange={onSkillsChange} skillsOnly />
        </Suspense>
      </ErrorBoundary>
      {peers.length > 0 && onOpenPeer ? (
        <SettingsGroup label={t`Messages between bots`}>
          <div data-testid="bot-settings-peers" className="-mx-2 flex flex-col">
            {peers.map((peer) => (
              <button
                key={peer.peerBotId}
                type="button"
                className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-2 text-start text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onOpenPeer(peer)}
              >
                <BotAvatar color={peer.color} identity={peer.peerBotId} size={24} />
                <span className="min-w-0 flex-1 truncate" dir="auto">
                  {peer.peerBotName}
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="size-4 text-muted-foreground rtl:rotate-180"
                />
              </button>
            ))}
          </div>
        </SettingsGroup>
      ) : null}
      <Collapsible
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        data-testid="bot-settings-advanced"
      >
        <h3>
          <CollapsibleTrigger className="group flex w-full cursor-pointer min-h-11 items-center justify-between gap-2 text-[13.5px] text-muted-foreground text-start outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
            <Trans>Advanced</Trans>
            <ChevronDown
              aria-hidden="true"
              className="size-4 text-muted-foreground transition-transform -rotate-90 group-aria-expanded:rotate-0 motion-reduce:transition-none"
            />
          </CollapsibleTrigger>
        </h3>
        <CollapsibleContent>
          <div className="space-y-3 pt-2">
            <Card className="gap-0 px-5 py-0">
              <button
                type="button"
                className="flex min-h-12 w-full items-center justify-between text-sm"
                onClick={() => {
                  setError(null);
                  onExport().catch((err) => setError(errorText(err)));
                }}
              >
                <Trans>Export</Trans>
                <Download className="size-4 text-muted-foreground" />
              </button>
            </Card>
            <Card className="gap-0 px-5 py-0 ring-destructive/30">
              <button
                type="button"
                className="min-h-12 w-full text-start text-sm text-destructive"
                onClick={onClear}
              >
                <Trans>Clear conversation</Trans>
              </button>
            </Card>
          </div>
        </CollapsibleContent>
      </Collapsible>
      {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
      {dirty ? (
        <Button
          className="sticky bottom-0 mt-5 w-full"
          disabled={saving}
          onClick={() => {
            void enqueueSave({
              name,
              title,
              description,
              color,
              notifyOnFinish,
            });
          }}
        >
          <Trans>Save</Trans>
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The backup list to send with a bot save. Only a list the person edited goes out, or the
 * default list when the bot gets a model of its own, so a save never rewrites a list nobody
 * touched. Going back to the default model sends nothing: the server drops the list with it.
 */
function backupsPatch(
  selected: { provider: string; modelId: string } | null,
  bot: Pick<Bot, "modelProvider" | "modelId">,
  draft: ModelBackupChoice[] | null,
  defaultList: ModelBackupChoice[] | null,
): { backupModels?: ModelBackupChoice[] } {
  if (!selected) return {};
  if (draft !== null) return { backupModels: draft };
  if (!bot.modelProvider || !bot.modelId) {
    return defaultList === null ? {} : { backupModels: defaultList };
  }
  return {};
}

function sameStringList(left: readonly string[], right: readonly string[]) {
  return left.length === right.length && left.every((entry, index) => entry === right[index]);
}

function catalogEntry(
  catalog: ModelCatalogEntry[],
  provider: string | null | undefined,
  modelId: string,
) {
  if (!provider) return undefined;
  return catalog.find((entry) => entry.provider === provider && entry.id === modelId);
}

function InlineProfileField({
  label,
  editLabel,
  value,
  placeholder,
  maxLength,
  className,
  onCommit,
}: {
  label: string;
  editLabel: string;
  value: string;
  placeholder?: string;
  maxLength: number;
  className: string;
  onCommit: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const cancelled = useRef(false);
  function commit() {
    if (cancelled.current) return;
    setEditing(false);
    if (draft.trim() !== value.trim()) onCommit(draft);
  }
  return editing ? (
    <Input
      autoFocus
      aria-label={label}
      value={draft}
      maxLength={maxLength}
      className={`h-auto border-transparent bg-transparent p-0 text-start shadow-none ${className}`}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit();
        }
        if (event.key === "Escape") {
          cancelled.current = true;
          setEditing(false);
        }
      }}
    />
  ) : (
    <button
      type="button"
      aria-label={editLabel}
      className={`-ms-2 min-h-8 min-w-0 max-w-full cursor-text [@media(pointer:coarse)]:min-h-11 break-words rounded px-2 text-start outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
      onClick={() => {
        cancelled.current = false;
        setDraft(value);
        setEditing(true);
      }}
    >
      {value || placeholder}
    </button>
  );
}

function SettingsGroup({
  label,
  action,
  children,
}: {
  label: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId}>
      <Card className="gap-0 px-5 py-4">
        <div className="flex min-h-8 items-center justify-between gap-3">
          <h3 id={titleId} className="text-[13.5px] text-muted-foreground">
            {label}
          </h3>
          {action}
        </div>
        <div className="mt-2 space-y-3 [&>*]:mt-0">{children}</div>
      </Card>
    </section>
  );
}
function humanizeTool(name: string) {
  const words = name.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
